import { SIMULATE_FAILURE_TTL_MS } from './simulate-failure.util';

/**
 * QA switch, companion to `simulate-failure.util.ts`: simulate a failed
 * form-definition (`role-N.form.json`) download, to smoke-test the sheet-load
 * toasts for `NET`, `ASSET` and `PARSE`.
 *
 * ## Why a second switch
 *
 * The SRV switch intercepts `HttpClient`. The schema load deliberately uses
 * native `fetch` (see `fetchSheetSchema`), which no interceptor can see, and
 * DevTools cannot produce a non-OK answer for it either: "Block request URL" and
 * Offline both make `fetch` REJECT, which is `NET`. This switch answers the
 * request itself.
 *
 * ## How a tester uses it
 *
 *   __simulateAsset('role-2', 'missing')       // file gone, host serves index.html -> ASSET
 *   __simulateAsset('role-2', 404)             // host answers a real 404 (rare)     -> ASSET
 *   __simulateAsset('role-2', 'invalid-json')  // unreadable contents                -> PARSE
 *   __simulateAsset('role-2', 'offline')       // request never completes            -> NET
 *   __simulateAsset('types.dict', 'missing')   // support files too: 'types.dict',
 *                                              //   'msic_options', 'scenarios.config'
 *   __simulateAssetClear()
 *
 * `'missing'` is the faithful "file not there" case: the deployed nginx has no
 * `location` for `.json`, so a missing asset answers `200 text/html` (the SPA
 * fallback), never a 404. `404` is kept for a host that does answer one.
 *
 * then open or switch to that sheet (tick "Disable cache" in the Network tab so a
 * previously downloaded copy is not reused). Same one-hour expiry, same
 * `simulateFailureEnabled` runtime flag, and same production strip as the SRV
 * switch. Pure: no DI, no storage access, so the rules are unit-testable.
 */

/** `localStorage` key. Outside the `mbrs-` prefix so logout does not clear it mid-test. */
export const SIMULATE_ASSET_STORAGE_KEY = 'yyc-simulate-asset-failure';

/**
 * A 4xx/5xx status (answered, non-OK → ASSET), or one of the special outcomes:
 * `'missing'` (200 HTML fallback → ASSET), `'invalid-json'` (→ PARSE), `'offline'` (→ NET).
 */
export type SimulateAssetMode = number | 'invalid-json' | 'offline' | 'missing';

/**
 * The non-schema JSON files a filing page loads, addressable by their file stem.
 * Matched exactly (never as a prefix of another name), unlike a `role-N` stem.
 */
export const SIMULATE_SUPPORT_ASSET_NAMES: readonly string[] = [
  'types.dict',
  'msic_options',
  'scenarios.config',
];

export interface SimulateAssetScenario {
  /** Schema file prefix, e.g. `role-2` (matches `role-2.form.json` and `role-2-mpers.form.json`). */
  role: string;
  mode: SimulateAssetMode;
  /** Epoch milliseconds after which the scenario is ignored. */
  expiresAt: number;
}

const ROLE_PATTERN = /^role-\d+(-\d+)?$/;
const MIN_FAILURE_STATUS = 400;
const MAX_FAILURE_STATUS = 599;

/**
 * Accepts `role-2` or `2` (returned as `role-2`), or one of the support-asset
 * names in {@link SIMULATE_SUPPORT_ASSET_NAMES}; null for anything else.
 */
export function normaliseSimulateAssetRole(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  if (SIMULATE_SUPPORT_ASSET_NAMES.includes(text)) return text;
  const role = /^\d/.test(text) ? `role-${text}` : text;
  return ROLE_PATTERN.test(role) ? role : null;
}

export function isSimulateAssetMode(value: unknown): value is SimulateAssetMode {
  if (value === 'invalid-json' || value === 'offline' || value === 'missing') return true;
  return Number.isInteger(value) && (value as number) >= MIN_FAILURE_STATUS && (value as number) <= MAX_FAILURE_STATUS;
}

/** Build the value the console helper stores. Throws on an unknown role or mode. */
export function createSimulateAssetScenario(role: unknown, mode: unknown, now: number): SimulateAssetScenario {
  const normalised = normaliseSimulateAssetRole(role);
  if (!normalised) {
    throw new Error(`Unknown role "${String(role)}". Use e.g. 'role-2' (or just 2).`);
  }
  if (!isSimulateAssetMode(mode)) {
    throw new Error(
      `Mode must be a 4xx/5xx status, 'missing', 'invalid-json' or 'offline' (got ${String(mode)})`,
    );
  }
  return { role: normalised, mode, expiresAt: now + SIMULATE_FAILURE_TTL_MS };
}

/** Parse the stored value; anything missing, malformed or expired is ignored, never thrown. */
export function parseSimulateAssetScenario(raw: string | null, now: number): SimulateAssetScenario | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const { role, mode, expiresAt } = parsed as Record<string, unknown>;
  if (normaliseSimulateAssetRole(role) !== role || !isSimulateAssetMode(mode)) return null;
  if (typeof expiresAt !== 'number' || expiresAt <= now) return null;
  return { role: role as string, mode, expiresAt };
}

/** Does this asset path belong to the scenario's role? `role-2` must not catch `role-20`. */
export function matchesSimulateAsset(scenario: SimulateAssetScenario, assetPath: string): boolean {
  const fileName = assetPath.split('?')[0].split('/').pop() ?? '';
  if (!fileName.startsWith(scenario.role)) return false;
  const next = fileName.charAt(scenario.role.length);
  return next === '.' || next === '-';
}

/**
 * The synthetic answer for a scenario that gets one, or null for `'offline'`
 * (the caller rejects the request instead — an answer is exactly what offline
 * does not have).
 */
export function buildSimulatedAssetResponse(scenario: SimulateAssetScenario): Response | null {
  if (scenario.mode === 'offline') return null;
  if (scenario.mode === 'missing') {
    // What the deployed nginx really answers for a file that is not there.
    return new Response('<!doctype html><html></html>', {
      status: 200,
      headers: { 'content-type': 'text/html' },
    });
  }
  if (scenario.mode === 'invalid-json') {
    return new Response('<<simulated unreadable form definition>>', { status: 200 });
  }
  return new Response('Simulated asset failure (QA switch)', {
    status: scenario.mode,
    statusText: 'Simulated asset failure (QA switch)',
  });
}
